import { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { Button } from '@design/components/button/Button';
import { EntityDetails } from '@design/widgets/entity-details/EntityDetails';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { TruncateTooltip } from '@terminal/components/truncate-tooltip/TruncateTooltip';
import { WatchlistTrigger } from '@terminal/components/watchlist/watchlist-trigger/WatchlistTrigger';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './EntityHeader.module.scss';

export interface EntityHeaderProps {
  className?: string;
  entity: Entity;
  shouldShowLogo?: boolean;
  priceDisplayNode?: ReactNode;
  truncateLongTitle?: boolean;
  truncateAtLength?: number;
  headerButton?: ReactNode;
  isDrawer?: boolean;
  rightNode?: ReactNode;
  hideButtonNode?: boolean;
}

const DEFAULT_ICON_SIZE = 48;

export const EntityHeader = ({
  className,
  entity,
  shouldShowLogo = true,
  priceDisplayNode,
  truncateLongTitle = false,
  truncateAtLength = 10,
  headerButton,
  isDrawer = false,
  rightNode,
  hideButtonNode = false,
}: EntityHeaderProps) => {
  const { t } = useTranslation('common');
  const ticker = formatTicker(entity);

  const entityName = entity.name_short || entity.name_full;

  const getWatchlistLabel = (isInWatchlist: boolean) => {
    if (headerButton) {
      return undefined;
    }

    return isInWatchlist
      ? t('common:components.watchlistBtn.watching')
      : t('common:components.watchlistBtn.addToWatchlistLabel');
  };

  return (
    <EntityDetails
      className={clsx(className, styles.styledEntityDetails)}
      entityNameComponent={
        truncateLongTitle ? (
          <TruncateTooltip
            placement="top"
            atLength={truncateAtLength}
            text={entityName}
            data-testid="analyze-header-title"
          />
        ) : (
          <span data-testid="text">{entityName}</span>
        )
      }
      priceDisplayNode={priceDisplayNode}
      formattedTicker={ticker}
      leftImgNode={
        shouldShowLogo && (
          <AssetLogoContainer
            className={clsx(styles.styledAssetLogoContainer, {
              [styles.drawer]: isDrawer,
            })}
            size={DEFAULT_ICON_SIZE}
            entity={entity}
          />
        )
      }
      rightNode={rightNode}
      buttonNode={
        !hideButtonNode && (
          <div className={styles.buttonContainer}>
            {headerButton}
            <WebOnlyFeat>
              <WatchlistTrigger
                tag={entity.tag}
                dropdownProps={{
                  strategy: 'fixed',
                  placement: 'top-start',
                  inPortal: false,
                }}
                renderTrigger={isInWatchlist => {
                  const watchlistLabel = isInWatchlist
                    ? t('common:components.watchlistBtn.watching')
                    : t('common:components.watchlistBtn.addToWatchlistLabel');

                  return (
                    <Button
                      className={clsx(styles.watchlistButton, {
                        [styles.active]: isInWatchlist,
                        [styles.roundIcon]: !!headerButton,
                      })}
                      data-testid="watchlist-button"
                      label={getWatchlistLabel(isInWatchlist)}
                      size="small"
                      variant="tertiary"
                      iconName={isInWatchlist ? 'Checkmark' : 'Add'}
                      aria-label={watchlistLabel}
                    />
                  );
                }}
              />
            </WebOnlyFeat>
          </div>
        )
      }
    />
  );
};
